import { styled } from '@linaria/react';
import { Flex, Group, Stack } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';
import { IntegrationAuthType } from '../../../api/integrations-client/integrations-client.type';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useIntegrationsStore } from '../../../store/integrations-store/integrations-store';
import { baseWhite, inkBase, primaryBase } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { H3, Text } from '../../../ui-library/typography/typography';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { getIntegrationType, isJDCIntegration } from '../integrations.helpers';
import { useBasicAuth, useCompleteOauth } from '../integrations.hooks';
import { JDC } from '../jdc/jdc';
import { AddNewIntegrationError } from './add-new-integration-error';
import { formatLogoSrc, getIntegrationName } from './add-new-integration.helpers';

/**
 * Success page for integration setup that also handles post-OAuth logic.
 * This component serves two purposes:
 * 1. Shows success message after completing integration setup
 * 2. Processes OAuth callbacks for all integration types (OAuth flow, JDC, error handling)
 *
 * Handles three flows:
 * 1. Initial integration setup - shows success page after OAuth completes
 * 2. Re-authentication - OAuth processor handles redirect to data management
 * 3. JDC integration - special case that uses legacy wizard-like interface
 */
export const AddNewIntegrationSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const integration = useIntegrationsStore((state) => state.integration);

  const integrationType = getIntegrationType(integration);
  const error = searchParams.get('error');
  const isJDC = isJDCIntegration(integration);

  // Mirrors the branch rendered below — an `error` renders AddNewIntegrationError,
  // so the tab must not report a successful integration.
  useDocumentTitle(error ? 'Integration Failed - Bloomfilter' : 'Integration Added - Bloomfilter');

  const isValidIntegration = !error && !isJDC && integrationType !== null;
  const isEnabledCompleteOauth = isValidIntegration && integration?.auth_type === IntegrationAuthType.Oauth;
  const isEnabledBasicAuth =
    isValidIntegration &&
    (integration?.auth_type === IntegrationAuthType.Basic || integration?.auth_type === IntegrationAuthType.Token);

  useCompleteOauth({ integrationType: integrationType!, enabled: isEnabledCompleteOauth });
  useBasicAuth({ integrationType: integrationType!, enabled: isEnabledBasicAuth });

  if (error) {
    return <AddNewIntegrationError error={error} />;
  }

  if (isJDC) {
    return <JDC />;
  }

  const integrationName = integration ? getIntegrationName(integration) : '';
  const logoSrc = integration?.logo ? formatLogoSrc(integration.logo) : undefined;

  const handleComplete = () => {
    navigate('/application/integrations');
  };

  const header = (
    <Flex align="center" gap={16}>
      <Button
        variant="secondary"
        radius="xl"
        leftSection={<Icon name="arrow_back" size={18} color={baseWhite} />}
        onClick={() => navigate('/application/integrations')}
      >
        {t('common.actions.back')}
      </Button>
      <Flex align="center" gap={8}>
        <Icon name="hub" size={32} />
        <H3>{t('integrations.add_new_integration')}</H3>
      </Flex>
    </Flex>
  );

  return (
    <MenuDrivenContainer header={header}>
      <PageContainer>
        <Stack align="center" justify="center" h="100%" pb={180}>
          <Stack gap={32}>
            <Group>
              <Icon name="check_circle" variant="filled" size={32} color={primaryBase} />
              <H3>{t('integrations.success.integration_started')}</H3>
            </Group>

            <Divider />

            <Stack>
              <Group gap={8}>
                {logoSrc && <IntegrationLogo src={logoSrc} alt={integrationName} />}
                <Text weight="bold" size="regular">
                  {integrationName}
                </Text>
              </Group>
              <Text size="regular" weight="regular" color={inkBase}>
                {t('integrations.success.integration_started_message')}
                <br />
                {t('integrations.success.email_notification')}
              </Text>
            </Stack>

            <Divider />

            <Button variant="primary" size="md" radius="xl" onClick={handleComplete} fullWidth>
              {t('integrations.success.complete_button')}
            </Button>
          </Stack>
        </Stack>
      </PageContainer>
    </MenuDrivenContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 150px;
  box-sizing: border-box;
  padding: 16px;
  background: ${baseWhite};
  border-radius: 8px;
  height: 100%;
  overflow-y: auto;
`;

const IntegrationLogo = styled.img`
  width: 40px;
  height: 40px;
`;
